// exceljs esta em dependencies; o resolver do build (craco/airbnb) gera um
// falso positivo de import/no-extraneous-dependencies, por isso o disable.
// eslint-disable-next-line import/no-extraneous-dependencies
import ExcelJS from 'exceljs';
import { Form } from 'store/states/forms/types';
import { Historic } from 'store/states/historics/types';
import { buildRows, createHeader, getExportFilename } from './csvUtils';

const MIN_COL_WIDTH = 12;
const MAX_COL_WIDTH = 60;
const LINE_HEIGHT = 15;
const HEADER_FILL_ARGB = 'FF4C7A2C';
const HEADER_FONT_ARGB = 'FFFFFFFF';
const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Nome de aba do Excel: max 31 chars e sem os caracteres * ? : \ / [ ]
function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[*?:\\/[\]]/g, ' ').trim();
  return (cleaned || 'Sheet1').slice(0, 31);
}

// Largura de coluna baseada na maior linha do conteudo (respeitando multi-linha),
// limitada por um minimo e um maximo para nao ficar nem apertada nem gigante.
function computeColumnWidth(header: string, cells: string[]): number {
  const longest = cells.reduce((max, cell) => {
    const lineMax = cell
      .split('\n')
      .reduce((m, line) => Math.max(m, line.length), 0);
    return Math.max(max, lineMax);
  }, header.length);

  return Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, longest + 2));
}

// Estima quantas linhas visuais a celula ocupa: quebras explicitas (\n) somadas
// ao wrap por largura da coluna. Usado para dimensionar a altura da linha, ja
// que o exceljs nao faz auto-fit e o Excel corta as linhas extras sem isso.
function countCellLines(value: string, width: number): number {
  if (!value) return 1;
  const usableWidth = Math.max(1, Math.floor(width));
  return value
    .split('\n')
    .reduce(
      (total, segment) =>
        total + Math.max(1, Math.ceil(segment.length / usableWidth)),
      0,
    );
}

function triggerDownload(buffer: ArrayBuffer, filename: string): void {
  const blob = new Blob([buffer], { type: XLSX_MIME });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Gera e baixa um .xlsx real (formato binario do Excel). Diferente do CSV, o
// XLSX nao sofre com separador/decimal, quebra de linha ou #NOME?: as celulas
// sao escritas como string e o Excel nunca as interpreta como formula.
export async function handleDataToGenerateXls(
  historics: Historic[],
  form: Form,
  locale: string,
): Promise<void> {
  const headers = createHeader(form);
  const rows = buildRows(historics, form, locale);

  const filename = getExportFilename(form);

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Agrosmart';

  const sheet = workbook.addWorksheet(sanitizeSheetName(filename), {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  // Linha de cabecalho: negrito, fundo verde, texto branco, centralizado.
  const headerRow = sheet.addRow(headers);
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: HEADER_FONT_ARGB } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: HEADER_FILL_ARGB },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
  });

  // Largura por coluna calculada antes das linhas para estimar a altura.
  const columnWidths = headers.map((header, index) =>
    computeColumnWidth(
      header,
      rows.map((row) => row[headers[index]] ?? ''),
    ),
  );

  // Linhas de dados: topo + quebra de texto. A altura e' setada manualmente
  // (exceljs nao faz auto-fit) para o Excel exibir TODAS as linhas da celula.
  rows.forEach((row) => {
    const values = headers.map((header) => row[header] ?? '');
    const dataRow = sheet.addRow(values);
    dataRow.alignment = { vertical: 'top', wrapText: true };

    const maxLines = values.reduce(
      (max, value, index) =>
        Math.max(max, countCellLines(value, columnWidths[index])),
      1,
    );
    dataRow.height = maxLines * LINE_HEIGHT;
  });

  // Filtro automatico sobre o cabecalho.
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length },
  };

  // Aplica as larguras calculadas.
  columnWidths.forEach((width, index) => {
    sheet.getColumn(index + 1).width = width;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(buffer as ArrayBuffer, `${filename}.xlsx`);
}
